import { useEffect } from 'react';
import { House, Upload, BarChart3, PenTool, BookOpen, Shield, Sparkles, LogOut, User } from 'lucide-react';
import { useAppStore } from './store';
import { client } from './api/client';
import LandingPage from './pages/LandingPage';
import UploadPage from './pages/UploadPage';
import AnalysisPage from './pages/AnalysisPage';
import PracticePage from './pages/PracticePage';
import CollectionPage from './pages/CollectionPage';
import AdminPage from './pages/AdminPage';
import AuthPage from './pages/AuthPage';

function App() {
  const { activeTab, setActiveTab, favorites, user, setUser, loadUserEssays, loadFavorites, startDuePractice, logout } = useAppStore();

  useEffect(() => {
    // Check session on mount
    client.auth.getSession().then(({ data }) => {
      if (data?.user) {
        setUser(data.user);
      } else {
        setUser(null);
      }
    });
  }, [setUser]);

  // Load user essays from backend when user logs in
  useEffect(() => {
    if (user) {
      loadUserEssays();
      loadFavorites();
    }
  }, [user, loadUserEssays, loadFavorites]);

  const handleLogout = async () => {
    await client.auth.signOut();
    logout();
  };

  if (activeTab === 'auth') {
    return <AuthPage />;
  }

  const tabs = [
    { id: 'home' as const, label: '主页', icon: House },
    { id: 'upload' as const, label: '上传作文', icon: Upload },
    { id: 'analysis' as const, label: '分析结果', icon: BarChart3 },
    { id: 'practice' as const, label: '练习', icon: PenTool },
    { id: 'collection' as const, label: '错题集', icon: BookOpen },
    { id: 'admin' as const, label: '管理台', icon: Shield },
  ];
  const visibleTabs = user
    ? tabs.filter((tab) => tab.id !== 'admin' || user.role === 'admin')
    : tabs.filter((tab) => tab.id === 'home');

  return (
    <div className="min-h-screen bg-surface">
      {/* Top navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between h-16">
          {/* Logo */}
          <button
            onClick={() => setActiveTab('home')}
            className="flex items-center gap-2 group"
          >
            <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
              <Sparkles size={16} className="text-white" />
            </div>
            <span className="font-serif text-xl text-primary tracking-tight">Refine 写作精修</span>
          </button>

          {/* Tab navigation */}
          <div className="flex items-center gap-1">
            {visibleTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id === 'practice' && user) {
                    startDuePractice();
                    return;
                  }
                  setActiveTab(tab.id);
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-primary text-white shadow-md'
                    : 'text-slate-500 hover:text-primary hover:bg-slate-100'
                }`}
              >
                <tab.icon size={16} />
                <span className="hidden sm:inline">{tab.label}</span>
                {tab.id === 'collection' && favorites.length > 0 && (
                  <span className={`text-xs font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full ${
                    activeTab === 'collection' ? 'bg-white text-primary' : 'bg-accent text-white'
                  }`}>
                    {favorites.length}
                  </span>
                )}
              </button>
            ))}

            <div className="w-px h-6 bg-slate-200 mx-2" />

            {user ? (
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 transition-all"
                title="退出登录"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">退出</span>
              </button>
            ) : (
              <button
                onClick={() => setActiveTab('auth')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white bg-primary hover:bg-primary/90 shadow-md transition-all"
              >
                <User size={16} />
                <span>登录</span>
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Page content */}
      <main>
        {activeTab === 'home' && <LandingPage />}
        {user && activeTab === 'upload' && <UploadPage />}
        {user && activeTab === 'analysis' && <AnalysisPage />}
        {user && activeTab === 'practice' && <PracticePage />}
        {user && activeTab === 'collection' && <CollectionPage />}
        {user && activeTab === 'admin' && user.role === 'admin' && <AdminPage />}
      </main>
    </div>
  );
}

export default App;
